<script setup lang="ts">
import { computed, type Component } from 'vue'
import { useContext } from './context'
import { type WithAttributes, type Labeled, toHtml, fw } from './attributes'

// #region props
const {
  is = 'a',
  uiNoUnderline = undefined,
  ...props
} = defineProps<WithAttributes<{
  /**
    * Pass `RouterLink` or use the `to()` helper
    * @default `<a>` tag
    */
  is?: Component | 'a'

  /**
    * Indicates the element that is visually and semantically current when the element is part of a nav.
    * Use 'step' for multi-part wizards, use 'location' for `#xy` anchors, otherwise use 'page'.
    * - `true`: Current.
    * - `'page'`: Current page.
    * - `'step'`: Current step in a multi-step process.
    * - `'location'`: Current location within an environment or context.
    * @see https://www.w3.org/TR/wai-aria-1.2/#aria-current
    */
  ariaCurrent?: 'page' | 'step' | 'location' | 'date' | 'time' | 'true' | 'false' | boolean
  ariaDescribedby?: string
  ariaDisabled?: 'true' | 'false' | boolean

  class: {
    align?: 'align-start' | 'align-stretch'
    justify?: 'justify-start'
    color?: 'color-primary' | 'color-destructive' | 'color-default' | 'color-blue' | 'color-yellow' | `${'color-primary' | 'color-destructive' | 'color-default' | 'color-blue' | 'color-yellow'}-solid` | `${'color-primary' | 'color-destructive' | 'color-default' | 'color-blue' | 'color-yellow'}-ghost` | `${'color-blue' | 'color-yellow'}-deep-solid` | `${'color-blue' | 'color-yellow'}-deep-ghost`
    shape?: 'shape-circle' | 'shape-square' | 'shape-field' | 'shape-field-grow' | 'shape-field-full' | 'shape-field-minimal'
  }

  uiBlockSize?: '24' | '40' | '48'
  uiNoUnderline?: boolean
} & Labeled>>()
// #endregion props

const context = useContext()

const uiBlockSize = computed(() =>
  props.class?.includes('shape-')
  || props.class?.includes('-ghost')
  || props.class?.includes('-solid')
  || props.uiBlockSize
    ? props.uiBlockSize ?? context.value.uiBlockSize ?? '48'
    : undefined
)
</script>

<template>
  <component
    :is
    :rel="is === 'a' ? 'noopener noreferrer' : undefined"
    v-bind="toHtml<'a'>(props)"
    :class="[
      uiBlockSize && fw.font.captionEmphasized,
      uiBlockSize && fw.layout.row,
      props.class,
      uiBlockSize && !props.class?.includes('shape-') && fw.shape.field,
      uiBlockSize && !props.class?.includes('color-') && fw.color.primaryGhost,
      uiBlockSize && !props.class?.includes('justify-') && fw.justify.center,
      uiBlockSize ? $style.block : $style.inline,
    ]"
    :ui-block-size
    :ui-no-underline
  >
    <slot />
  </component>
</template>

<style module>
.block,
.inline {
  &:not([href]) {
      opacity: .5;
  }
  gap: 8px;
}

/* Block (Edit in conjunction with `Button.vue`) */
.block {
    /**
    * ====================================================
    * Color transitions
    * ====================================================
    */

    transition: background-color .3s;

    &:hover, &:focus-visible {
      transition: none;
    }

    &:is([aria-pressed=true], [aria-expanded=true], [aria-selected=true], [aria-checked=true]):hover:not(:active){
      transition: background-color .3s;
    }

  &:not(:hover):not([ui-no-underline]) {
    text-decoration-color: color-mix(in oklab, transparent 50%, currentcolor)
  }

  /**
  * ====================================================
  * Layout
  * ====================================================
  */

  position: relative;
  font-size: 14px;

  /* Badge placement */
  --badge-inset: -3px -3px auto auto;

  &[ui-block-size='40'] {
  padding: 0 12px;
  --badge-inset: -4px -4px auto auto;
}

  &[ui-block-size='24'] {
  padding: 0 4px;
  --badge-inset: -10px -10px auto auto;
}

  &:global(.shape-circle), &:global(.shape-square) {
    --badge-inset: -4px -4px auto auto;

    &[ui-block-size='40'] {
    padding: 0 4px;
    --badge-inset: -5px -5px auto auto;
  }

    &[ui-block-size='24'] {
    padding: 0 4px;
    --badge-inset: -9px -9px auto auto;
  }
  }
  /**

  * ====================================================
  * Interactivity
  * ====================================================
  */

  /* Add transparent hit area at least 44px squared to satisfy WCAG2 target size requirement AAA: */
  &::after {
    content: '';
    position: absolute;

    inset: 50%;
    transform: translate(-50%, -50%);

    inline-size: max(48px, 100%);
    block-size: max(48px, 100%);
  }

  /* Remove underline affordance always (by default, links show underline at least on hover and focus)*/
  &[ui-no-underline] {
    text-decoration: none;
  }
}

/* Inline */
.inline {
  display: inline-flex;
  align-items: center;
  border-radius: 2px;
  transition: color 0.1s, background-color 0.1s;
  --inline-gap: 0;

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 4px;
    &:has(>img) {
      /* Hide focus indicator ring when link contains image to avoid very large shapes - in that case, underline is the only affordance */
      outline-color: transparent;
    }
  }
}
</style>
